import React from 'react';
import { Box, Paper, Stack, Button, Typography } from '@mui/material';
import { PlayCircleOutline, Edit, Save, Cancel as CancelIcon } from '@mui/icons-material';
import Editor from '@monaco-editor/react';
import { getLanguageConfig } from '../../../../../utils/code/languageConfig';

/**
 * 公共测试用例编辑器组件
 * 用于查看和编辑公共测试用例（教师可编辑，学生只能查看）
 */
function PublicTestEditor({
  question,
  testCode,
  onTestCodeChange,
  editing,
  onEdit,
  onSave,
  onCancel,
  onRun,
  running = false,
  saving = false,
  isTeacher = false
}) {
  const hasTestCode = question?.publicTestCode || isTeacher;

  if (!hasTestCode) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.disabled">
          No public test cases available
        </Typography>
      </Box>
    );
  }

  const displayCode = editing ? testCode : (question?.publicTestCode || '');
  const language = question ? getLanguageConfig(question.language || 'python').monacoLanguage : 'python';

  const editorOptions = {
    readOnly: !editing || !isTeacher,
    domReadOnly: !editing || !isTeacher,
    minimap: { enabled: false },
    fontSize: 13,
    lineNumbers: 'on',
    lineNumbersMinChars: 3,
    scrollBeyondLastLine: false,
    automaticLayout: true,
    tabSize: 4,
    wordWrap: 'off',
    fontFamily: "'Monaco', 'Menlo', 'Consolas', 'Courier New', monospace",
    renderWhitespace: 'selection',
    bracketPairColorization: { enabled: true },
    lineHeight: 19,
    letterSpacing: 0.5,
    scrollbar: {
      vertical: 'auto',
      horizontal: 'auto'
    },
    cursorStyle: 'line-thin',
    contextmenu: editing && isTeacher,
    quickSuggestions: editing && isTeacher,
    parameterHints: { enabled: editing && isTeacher },
    suggestOnTriggerCharacters: editing && isTeacher,
    acceptSuggestionOnEnter: editing && isTeacher ? 'on' : 'off',
    tabCompletion: editing && isTeacher ? 'on' : 'off',
    wordBasedSuggestions: editing && isTeacher
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          flexGrow: 1,
          position: 'relative',
          bgcolor: editing ? 'white' : '#f8f9fa',
          overflow: 'hidden',
          p: 2
        }}
      >
        <Editor
          height="100%"
          language={language}
          value={displayCode}
          onChange={onTestCodeChange}
          theme="light"
          options={editorOptions}
        />
      </Box>
      <Paper
        sx={{
          px: 2,
          py: 0.5,
          borderRadius: 0,
          bgcolor: 'grey.100',
          borderTop: 1,
          borderColor: 'divider',
          minHeight: 36,
          display: 'flex',
          alignItems: 'center'
        }}
        elevation={0}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%' }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => onRun('public')}
            disabled={running}
            color="primary"
            startIcon={<PlayCircleOutline />}
          >
            {running ? 'Running...' : 'Run with public test'}
          </Button>
          {isTeacher && (
            <Stack direction="row" spacing={1}>
              {!editing ? (
                <Button
                  size="small"
                  startIcon={<Edit />}
                  onClick={onEdit}
                >
                  编辑
                </Button>
              ) : (
                <>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<Save />}
                    onClick={onSave}
                    disabled={saving}
                  >
                    保存
                  </Button>
                  <Button
                    size="small"
                    startIcon={<CancelIcon />}
                    onClick={onCancel}
                    disabled={saving}
                  >
                    取消
                  </Button>
                </>
              )}
            </Stack>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}

export default PublicTestEditor;

